import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useLocation } from 'react-router-dom';
import { useMatchMedia } from '@hooks/useMatchMedia';

const COLLAPSED_KEY = 'd-orca.sidebar.collapsed';

/** Below this width the sidebar starts collapsed and expands as an overlay, so
 *  the content pane keeps the full width beside the 64px rail. The ONE place
 *  the shell's narrow breakpoint is defined: the hook publishes the result as
 *  `body[data-viewport='narrow']`, and the shell's CSS keys off that attribute
 *  instead of repeating the width in its own media queries. */
export const SIDEBAR_OVERLAY_QUERY = '(width < 1024px)';

/** The stored desktop preference. Storage can be unavailable (a privacy mode,
 *  blocked site data) — the sidebar then simply starts expanded. */
function readStoredCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

function storeCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, String(collapsed));
  } catch {
    // Not persisted this time; the in-memory state is unaffected.
  }
}

interface ISidebarCollapse {
  collapsed: boolean;
  /** Narrow viewport with the sidebar expanded — it floats over the content. */
  overlay: boolean;
  setCollapsed: Dispatch<SetStateAction<boolean>>;
}

/**
 * The sidebar's one collapse state. On desktop it is the user's manual choice,
 * persisted. On a narrow viewport it is forced collapsed on entry and after
 * every navigation; expanding it there opens an overlay that is not persisted,
 * so leaving the narrow range restores the stored desktop preference.
 */
export function useSidebarCollapse(): ISidebarCollapse {
  const { pathname } = useLocation();
  const narrow = useMatchMedia(SIDEBAR_OVERLAY_QUERY);
  const [collapsed, setCollapsed] = useState<boolean>(() => narrow || readStoredCollapsed());
  const [prevNarrow, setPrevNarrow] = useState(narrow);
  const [prevPathname, setPrevPathname] = useState(pathname);

  if (narrow !== prevNarrow) {
    setPrevNarrow(narrow);
    setCollapsed(narrow || readStoredCollapsed());
  }
  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    if (narrow) setCollapsed(true);
  }

  const overlay = narrow && !collapsed;

  // Layout facts for the shell's CSS. An overlay leaves the content laid out
  // beside the collapsed rail, so the rail width follows `collapsed || narrow`.
  useEffect(() => {
    document.body.dataset.sidebarCollapsed = collapsed || narrow ? 'true' : 'false';
    document.body.dataset.viewport = narrow ? 'narrow' : 'wide';
    return () => {
      delete document.body.dataset.sidebarCollapsed;
      delete document.body.dataset.viewport;
    };
  }, [collapsed, narrow]);

  // Only a desktop choice is the user's preference; the narrow range's
  // forced collapse and its overlay are never stored.
  useEffect(() => {
    if (!narrow) storeCollapsed(collapsed);
  }, [collapsed, narrow]);

  return { collapsed, overlay, setCollapsed };
}
